import { defineComponent as _defineComponent } from 'vue'
import ECharts from 'vue-echarts';
import { WidgetEnum } from '@/models/enums/WidgetEnum';
import WidgetDefinition from '@/models/Charts/widgetDefinition';
import { Aggregate, numberToArray } from '@/models/metrics/aggregates';
import { Routes } from '@/router/routes';
import { TargetsMetric } from '@/models/enums/TargetsMetric';
import { isEmpty, isNil } from '@/utils/misc';
import { devicesService } from '@/services/devices.service';
import { shiftsService } from '@/services/shifts.service';
import * as maxCuttingTime from '@/components/Charts/maxCuttingTime';
import { ChartGenerator } from '@/models/Charts/abstract/chartGenerator';
import { GeneratorParams } from '@/models/Charts/generatorParams';
import WidgetFactory from '@/models/Charts/widgetFactory';
import { computed, onMounted, ref, watch } from 'vue';
import { useAutoRemovedResizeListener } from '@/composables/misc';
import { useRouter } from '@/composables/router';
import { EChartsOption } from 'echarts';
import { currentLocale } from '@/utils/i18n';
import { getTargets } from './widgetContainer/targets';
import { useOnLongTap } from '@/composables/mouse';
import { useI18n } from '@/composables/i18n';


/** @deprecated THIS COMPONENT IS FROZEN **/
const __default__ = {};

export default /*#__PURE__*/_defineComponent({
  ...__default__,
  __name: 'Chart',
  props: {
    widgetDefinition: null,
    generator: null,
    data: null,
    paramValues: null,
    loading: { type: Boolean },
    selfManagedFilters: { type: Boolean, default: false },
    customerNo: null
  },
  emits: ["double-click-expand"],
  setup(__props: any, { emit }: { emit: ((e: 'double-click-expand', value: any) => void), expose: any, slots: any, attrs: any }) {

const props = __props as { widgetDefinition: WidgetDefinition, generator: ChartGenerator<any>, data: any, paramValues: Record<string, any>, loading: boolean, selfManagedFilters: boolean, customerNo?: string };




const router = useRouter();
const { mouseDown, mouseUp } = useOnLongTap((event) => {
  onExpandOnDblClick(event);
});

const generatorParams = ref<GeneratorParams>({});
const options = ref<any>(null);
const divRef = ref<HTMLDivElement>();
const chartRef = ref<InstanceType<typeof ECharts>>();

const { t } = useI18n();

// When switching back and forth from and to fullscreen mode (F11), widgets
// might overflow the card vertically. We don't know the cause but using
// this resize listener fixes it.
useAutoRemovedResizeListener(onResize);

onMounted(() => {
  updateOptions();
});

function updateOptions() {
  if (isEmpty(props.data)) {
    return;
  }

  generatorParams.value = {
    ...WidgetFactory.createOptions(props.widgetDefinition.widget),
    clientWidth: divRef.value!.clientWidth,
    clientHeight: divRef.value!.clientHeight,
    targets: isNil(targetsMetric.value)
      ? undefined
      : getTargets(
          props.selfManagedFilters,
          props.widgetDefinition.target,
          targetsMetric.value,
          props.widgetDefinition.widget,
        ),
    aggregates: aggregates.value,
    paramValues: props.paramValues,
    customerNo: props.customerNo,
    timeFilter: props.widgetDefinition.timeFilterEnumValue,
    timeAxisSpan: props.widgetDefinition.axisSpan,
  };

  if (
    props.widgetDefinition.widget === WidgetEnum.LaserCuttingTime ||
    props.widgetDefinition.widget === WidgetEnum.TubeCuttingTime
  ) {
    const deviceIds = devicesService.store.deviceIdsToIds(
      props.widgetDefinition.deviceId as string[],
    );
    const selectedShifts = shiftsService.store.getMatchingIds(
      props.widgetDefinition.shifts,
    );
    generatorParams.value.maxValue = maxCuttingTime.calculateMaxCuttingTime(
      deviceIds,
      selectedShifts,
      props.widgetDefinition.timeFilter,
    );
  }

  // Some constructors inadvertently mutate the original data (using .sort() in arrays, for example).
  // If data is mutated from within the generator, this function is recursively called by the watcher.
  // If a clone of the data is passed, we don't need to worry about mutating data, and the issue is prevented.
  const dataClone = Array.isArray(props.data)
    ? props.data.concat()
    : { ...props.data };
  options.value = {
    textStyle: { fontFamily: 'Inter' },
    ...props.generator?.updateOptions(
      dataClone,
      generatorParams.value,
      chartRef.value?.getOption() as EChartsOption,
    ),
  };
}

// FIXME: Code duplication with WorkCenterConsoleOverview.goToDevice
function onClickEvent(params: any) {
  if (props.widgetDefinition.widget === WidgetEnum.FactoryOverview) {
    let route;
    if (params.name.startsWith('L-')) {
      route = Routes.Cut;
    } else if (params.name.startsWith('B-')) {
      route = Routes.Bend;
    } else if (params.name.startsWith('T-')) {
      route = Routes.Tubes;
    } else {
      return;
    }

    router.push({
      name: route,
      // FIXME: Use URL query parameters instead
      params: {
        selectedDeviceStringId: params.name,
      },
    });
  }
}

function onLegendSelectChanged(params: any) {
  if (
    props.widgetDefinition.widget ===
    WidgetEnum.TechnologyDrawerInformationTimeline
  ) {
    options.value = {
      textStyle: { fontFamily: 'Inter' },
      ...props.generator?.updateOptions(props.data, {
        ...generatorParams.value,
        removedSecondsSelected: params.selected.removed_seconds,
        pdSelected: params.selected.pd,
        powerAmplitudeSelected: params.selected.power_amplitude,
      }),
    };
  } else if (
    props.widgetDefinition.widget === WidgetEnum.LaserOutputAndScrapTimeline ||
    props.widgetDefinition.widget === WidgetEnum.TubeOutputScrapHist
  ) {
    options.value = {
      textStyle: { fontFamily: 'Inter' },
      ...props.generator?.updateOptions(props.data, {
        ...generatorParams.value,
        legendParams: params,
      }),
    };
  }
}

function getWidgetTargetMetric(widget: WidgetEnum): TargetsMetric {
  let widgetString = Object.keys(WidgetEnum).find(
    (key: string) => WidgetEnum[key as keyof typeof WidgetEnum] === widget,
  )!;

  // HACK (temporary): We don't want a target for LaserAvailability pie chart,
  // but it will be added in the future.
  if (widgetString !== 'LaserProductivityHist') {
    widgetString = widgetString.replace('Hist', '');
  }

  return widgetString in TargetsMetric
    ? TargetsMetric[widgetString as keyof typeof TargetsMetric]
    : TargetsMetric.None;
}

function onResize() {
  chartRef.value?.resize();
}

function onExpandOnDblClick(params: any) {
  if (canExpandOnDblClick.value) {
    emit('double-click-expand', params);
  }
}

const aggregates = computed((): Aggregate[] =>
  numberToArray(props.widgetDefinition.aggregates),
);

const echartsUpdateOptions = computed((): any => ({ notMerge: true }));

const canExpandOnDblClick = computed((): boolean =>
  props.widgetDefinition.canExpandOnDblClick(),
);

const targetsMetric = computed((): TargetsMetric | null =>
  !isNil(props.widgetDefinition)
    ? getWidgetTargetMetric(props.widgetDefinition.widget)
    : null,
);

const showChart = computed(
  (): boolean => !isEmpty(props.data) && !isNil(options.value),
);

const locale = computed(() => currentLocale());

watch(
  () => props.widgetDefinition,
  (newValue: WidgetDefinition, oldValue: WidgetDefinition) => {
    if (newValue.widget !== oldValue.widget) {
      chartRef.value?.clear();
    }
    updateOptions();
  },
);

watch(
  () => [props.data, locale.value],
  () => {
    updateOptions();
  },
);

return { __sfc: true,props, emit, router, mouseDown, mouseUp, generatorParams, options, divRef, chartRef, t, updateOptions, onClickEvent, onLegendSelectChanged, getWidgetTargetMetric, onResize, onExpandOnDblClick, aggregates, echartsUpdateOptions, canExpandOnDblClick, targetsMetric, showChart, locale }
}

})